import { useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';

import { cx } from './cx';

interface FileDropzoneProps {
  accept?: string;
  onFile: (file: File) => void;
  hint?: string;
  label?: string;
}

function UploadIcon() {
  return (
    <svg aria-hidden="true" fill="none" height="20" viewBox="0 0 20 20" width="20">
      <path
        d="M10 13V4m0 0L6.5 7.5M10 4l3.5 3.5M4 13.5V15a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 16 15v-1.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}

/** Dashed-border upload target with a circular icon; drag-and-drop or click. */
export function FileDropzone({ accept, onFile, hint, label }: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  function openPicker() {
    inputRef.current?.click();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files[0];

    if (file) {
      onFile(file);
    }
  }

  return (
    <div
      aria-label={label ?? 'Upload a file'}
      className={cx('rfp-dropzone', isDragging && 'rfp-dropzone--dragging')}
      onClick={openPicker}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <input
        accept={accept}
        className="rfp-dropzone__input"
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (file) {
            onFile(file);
          }

          event.target.value = '';
        }}
        ref={inputRef}
        type="file"
      />
      <span className="rfp-dropzone__icon">
        <UploadIcon />
      </span>
      <span className="rfp-dropzone__title">{label ?? 'Drop a file here or click to browse'}</span>
      {hint && <span className="rfp-dropzone__hint">{hint}</span>}
    </div>
  );
}
